import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import 'suneditor/dist/css/suneditor.min.css';
import { storyAPI } from '../shared/services/api';
import { useAuth } from '../features/auth/contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import StarRating from '../components/StarRating';
import RatingModal from '../components/RatingModal';
import CommentModal from '../components/CommentModal';
import CommentsSection from '../components/CommentsSection';
import ShareModal from '../components/ShareModal';
import TextToSpeechControls from '../components/TextToSpeechControls';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { StoryComment } from '../shared/types';
import { sanitizeStoryContent } from '../utils/security';

const StoryContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const StoryCard = styled.article`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
`;

const StoryHeader = styled.header`
  padding: 2rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  text-align: center;
`;

const StoryTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-gray-900);
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StoryMeta = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  clear: both;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-gray-700);
  font-weight: 500;
`;

const StoryGenre = styled.span`
  background: var(--color-white);
  color: var(--color-gray-800);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  box-shadow: var(--shadow-sm);
`;

const StoryRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-gray-700);
  font-weight: 500;
`;

const StoryDates = styled.div`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  text-align: center;
`;

const StorySummary = styled.div`
  padding: 2rem;
  border-bottom: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
`;

const SummaryTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 1rem;
`;

const SummaryText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--color-gray-700);
`;


const StoryContent = styled.div`
  padding: 2rem;
  font-family: var(--font-serif);
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--color-gray-800);

  /* SunEditor content preservation */
  & > * {
    max-width: 100%;
  }

  /* Headers */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-serif);
    margin: 1rem 0;
    color: var(--color-gray-900);
    line-height: 1.3;
  }

  h1 { font-size: 2em; font-weight: bold; }
  h2 { font-size: 1.75em; font-weight: bold; }
  h3 { font-size: 1.5em; font-weight: bold; }
  h4 { font-size: 1.25em; font-weight: bold; }
  h5 { font-size: 1.1em; font-weight: bold; }
  h6 { font-size: 1em; font-weight: bold; }

  /* Paragraphs */
  p {
    margin-bottom: 1rem;
    line-height: 1.8;
  }

  /* Text formatting */
  strong, b { font-weight: bold; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  s, strike, del { text-decoration: line-through; }
  sub { vertical-align: sub; font-size: smaller; }
  sup { vertical-align: super; font-size: smaller; }

  /* Lists */
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }

  /* Blockquotes */
  blockquote {
    margin: 1rem 0;
    padding: 1rem 1.5rem;
    border-left: 4px solid var(--color-primary);
    background: var(--color-gray-50);
    font-style: italic;
    color: var(--color-gray-700);
    border-radius: 0 4px 4px 0;
  }

  /* Links */
  a {
    color: var(--color-primary);
    text-decoration: underline;
  }

  a:hover {
    color: var(--color-primary-dark, #2563eb);
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1rem 0;
  }

  /* Support for SunEditor image float and text wrapping */
  .se-component.se-image-container {
    margin: 0.5rem 0 !important;
    display: inline-block !important;
    width: auto !important;
    max-width: none !important;
    clear: none !important;
    
    /* Handle left float */
    &.__se__float-left,
    &[data-align="left"] {
      float: left !important;
      margin: 0.5rem 1rem 0.5rem 0 !important;
      clear: none !important;
    }
    
    /* Handle right float */
    &.__se__float-right,
    &[data-align="right"] {
      float: right !important;
      margin: 0.5rem 0 0.5rem 1rem !important;
      clear: none !important;
    }
    
    /* Ensure figure maintains proper styling */
    figure {
      margin: 0 !important;
      display: block !important;
      width: auto !important;
      float: none !important;
    }
  }
  
  /* Override any clearing on elements that should wrap around images */
  p,
  div:not(.se-component),
  h1, h2, h3, h4, h5, h6 {
    clear: none !important;
    overflow: visible !important;
  }

  /* Clear floats at the end of content */
  &::after {
    content: "";
    display: table;
    clear: both;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }

  th, td {
    border: 1px solid var(--color-gray-300);
    padding: 0.75rem;
    text-align: left;
  }

  th {
    background: var(--color-gray-100);
    font-weight: bold;
  }

  /* Code */
  code {
    background: var(--color-gray-100);
    padding: 0.25rem 0.5rem;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }

  pre {
    background: var(--color-gray-100);
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 1rem 0;
  }

  pre code {
    background: none;
    padding: 0;
  }

  /* Text alignment classes from SunEditor */
  .se-align-left { text-align: left; }
  .se-align-center { text-align: center; }
  .se-align-right { text-align: right; }
  .se-align-justify { text-align: justify; }

  /* Color styling */
  span[style*="color"] {
    /* Preserve inline color styles from SunEditor */
  }

  span[style*="background-color"] {
    /* Preserve inline background color styles from SunEditor */
    padding: 0.1em 0.2em;
    border-radius: 2px;
  }

  /* Font size classes */
  .se-fs-8 { font-size: 8px; }
  .se-fs-9 { font-size: 9px; }
  .se-fs-10 { font-size: 10px; }
  .se-fs-11 { font-size: 11px; }
  .se-fs-12 { font-size: 12px; }
  .se-fs-14 { font-size: 14px; }
  .se-fs-16 { font-size: 16px; }
  .se-fs-18 { font-size: 18px; }
  .se-fs-24 { font-size: 24px; }
  .se-fs-36 { font-size: 36px; }

  @media (max-width: 768px) {
    padding: 1.5rem;
    font-size: 1rem;
    
    table {
      font-size: 0.9rem;
    }
    
    th, td {
      padding: 0.5rem;
    }
  }
`;

const ActionButtons = styled.div`
  padding: 2rem;
  background: var(--color-gray-50);
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ErrorMessage = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 3rem;
  text-align: center;
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-700);
  margin-bottom: 0.5rem;
`;

const ErrorText = styled.p`
  color: var(--color-gray-500);
  margin-bottom: 2rem;
`;

const StoryView: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { keycloak, user, login } = useAuth();
  const [story, setStory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [userRating, setUserRating] = useState<{rating: number, comment?: string} | null>(null);
  const [comments, setComments] = useState<StoryComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const tts = useTextToSpeech({
    storyId: id || '',
    title: story?.title || '',
    content: story?.content?.html || story?.content?.text || ''
  });

  useEffect(() => {
    if (id) {
      loadStory();
    }
  }, [id]);

  const loadStory = async () => {
    if (!id) return;

    try {
      // Use appropriate endpoint based on authentication status
      const response = keycloak?.authenticated 
        ? await storyAPI.getStory(id)
        : await storyAPI.getPublicStory(id);
      setStory(response.data);
      
      // Load user's rating if authenticated
      if (keycloak?.authenticated) {
        try {
          const ratingResponse = await storyAPI.getMyRating(id);
          setUserRating(ratingResponse.data);
        } catch (ratingError: any) {
          // User hasn't rated this story yet - this is normal
          if (ratingError.response?.status !== 404) {
            console.error('Failed to load user rating:', ratingError);
          }
        }
      }
      
      // Load comments only if story loaded successfully
      await loadComments();
    } catch (error: any) {
      console.error('Failed to load story:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to view this story.');
      } else if (error.response?.status === 404) {
        setError('Story not found.');
      } else {
        setError('Failed to load story. Please try again.');
      }
      // Clear comments if story failed to load
      setComments([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadComments = async () => {
    if (!id) return;
    
    try {
      setLoadingComments(true);
      const response = keycloak?.authenticated 
        ? await storyAPI.getComments(id)
        : await storyAPI.getPublicComments(id);
      setComments(response.data);
    } catch (error: any) {
      console.error('Failed to load comments:', error);
      // Don't set error state for comments - just clear comments if story doesn't exist
      if (error.response?.status === 404) {
        setComments([]);
      }
    } finally {
      setLoadingComments(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBackClick = () => {
    // Use browser's back history if available, otherwise go to home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleRatingSubmit = async (rating: number) => {
    if (!id || !keycloak?.authenticated || submittingRating) return;

    try {
      setSubmittingRating(true);
      
      if (rating > 0) {
        const response = await storyAPI.rateStory(id, rating, '');
        
        // Update the story with new rating data immediately
        setStory(response.data);
        setUserRating({ rating, comment: '' });
        
        // Show success message
        toast.success(`Thank you for rating this story ${rating} star${rating > 1 ? 's' : ''}!`);
      } else {
        throw new Error('Please select a rating before submitting.');
      }
    } catch (error: any) {
      console.error('Failed to submit rating:', error);
      throw error; // Let the modal handle the error display
    } finally {
      setSubmittingRating(false);
    }
  };
  
  const handleCommentSubmit = async (comment: string) => {
    if (!id || !keycloak?.authenticated || submittingComment || !story) return;

    try {
      setSubmittingComment(true);
      await storyAPI.addComment(id, comment.trim());
      await loadComments(); // Reload comments to show the new one
    } catch (error: any) {
      console.error('Failed to submit comment:', error);
      throw error; // Let the modal handle the error display
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <StoryContainer>
        <ErrorMessage>
          <ErrorIcon>📚</ErrorIcon>
          <ErrorTitle>Story Unavailable</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <button onClick={handleBackClick} className="btn btn-primary">
            Back to Home
          </button>
        </ErrorMessage>
      </StoryContainer>
    );
  }

  if (!story) {
    return (
      <StoryContainer>
        <ErrorMessage>
          <ErrorIcon>🔍</ErrorIcon>
          <ErrorTitle>Story Not Found</ErrorTitle>
          <ErrorText>The story you're looking for doesn't exist.</ErrorText>
          <button onClick={handleBackClick} className="btn btn-primary">
            Back to Home
          </button>
        </ErrorMessage>
      </StoryContainer>
    );
  }

  const isAuthor = user && story.author.id === user.id;
  const canEdit = isAuthor; // In the future, this could include collaborators

  return (
    <StoryContainer>
      <StoryCard>
        {/* Title */}
        <StoryHeader>
          <StoryTitle>{story.title}</StoryTitle>
        </StoryHeader>

        {/* Story content and metadata */}
        <div style={{ borderTop: '1px solid var(--color-gray-200)' }}>
          <StoryContent
            dangerouslySetInnerHTML={{
              __html: sanitizeStoryContent(story.content?.html || '<p>No content available.</p>')
            }}
          />

          {/* Text to Speech Controls */}
          <div style={{ padding: '0 2rem' }}>
            <TextToSpeechControls
              storyId={story.id}
              title={story.title}
              content={story.content?.html || story.content?.text || ''}
              isPlaying={tts.isPlaying}
              isPaused={tts.isPaused}
              isLoading={tts.isLoading}
              progress={tts.progress}
              currentText={tts.currentText}
              currentSentence={tts.currentSentence}
              currentPosition={tts.currentPosition}
              totalLength={tts.totalLength}
              onPlay={tts.play}
              onPause={tts.pause}
              onResume={tts.resume}
              onStop={tts.stop}
              onSetPosition={tts.setPosition}
            />
          </div>
          
          <div style={{ padding: '2rem', borderTop: '1px solid var(--color-gray-200)' }}>
            <StoryMeta>
              <AuthorInfo>
                👤 by {story.author.fullName || story.author.username}
              </AuthorInfo>
              <StoryGenre>{story.genre}</StoryGenre>
              <StoryRating>
                <StarRating
                  currentRating={parseFloat(story.rating)}
                  disabled={true}
                  showText={false}
                  size="sm"
                />
                <span style={{ marginLeft: '0.5rem' }}>
                  {parseFloat(story.rating).toFixed(1)} ({story.ratingCount} {story.ratingCount === 1 ? 'rating' : 'ratings'}) • 👁️ {story.viewCount} {story.viewCount === 1 ? 'view' : 'views'}
                </span>
              </StoryRating>
            </StoryMeta>
            <StoryDates>
              Created {formatDate(story.createdAt)}
              {story.updatedAt !== story.createdAt && (
                <> • Updated {formatDate(story.updatedAt)}</>
              )}
            </StoryDates>
          </div>
        </div>

        {/* Action buttons */}
        <ActionButtons>
          {canEdit && (
            <Link to={`/story/edit/${story.id}`} className="btn btn-primary">
              ✏️ {t('story.editStory')}
            </Link>
          )}
          
          {keycloak?.authenticated && !isAuthor && (
            <>
              {!userRating && (
                <button 
                  className="btn btn-primary"
                  onClick={() => setIsRatingModalOpen(true)}
                  disabled={submittingRating}
                >
                  ⭐ {t('story.rateStory')}
                </button>
              )}
              
              <button 
                className="btn btn-outline"
                onClick={() => setIsCommentModalOpen(true)}
                disabled={submittingComment}
              >
                💬 {t('story.addComment')}
              </button>
            </>
          )}
          
          {keycloak?.authenticated && isAuthor && (
            <button 
              className="btn btn-outline"
              onClick={() => setIsCommentModalOpen(true)}
              disabled={submittingComment}
            >
              💬 Add Comment
            </button>
          )}
          
          {!keycloak?.authenticated && (
            <div style={{ textAlign: 'center', background: 'var(--color-gray-100)', borderRadius: 'var(--border-radius-md)' }}>
              <button 
                className="btn btn-primary"
                onClick={() => login()}
                title="Want to rate or comment on this story?"
              >
                🔑 {t('story.loginToInteract')}
              </button>
            </div>
          )}
          
          <button 
            className="btn btn-outline"
            onClick={() => setIsShareModalOpen(true)}
          >
            📤 {t('story.shareStory')}
          </button>
          
          <button onClick={handleBackClick} className="btn btn-outline">
            ← Back
          </button>
        </ActionButtons>
        
        {/* Comments Section - only show if story exists */}
        <StoryCard style={{ marginTop: '2rem' }}>
          <CommentsSection
            comments={comments}
            onAddComment={() => setIsCommentModalOpen(true)}
            canComment={!!keycloak?.authenticated}
            loading={loadingComments}
          />
        </StoryCard>
      </StoryCard>
      
      {/* Rating Modal - only show if story exists */}
      {story && (
        <RatingModal
          isOpen={isRatingModalOpen}
          onClose={() => setIsRatingModalOpen(false)}
          onSubmit={handleRatingSubmit}
          storyTitle={story.title}
          currentRating={userRating?.rating || 0}
          isSubmitting={submittingRating}
          hasAlreadyRated={!!userRating}
        />
      )}
      
      {/* Comment Modal - only show if story exists */}
      {story && (
        <CommentModal
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          onSubmit={handleCommentSubmit}
          storyTitle={story.title}
          isSubmitting={submittingComment}
        />
      )}
      
      {/* Share Modal - only show if story exists */}
      {story && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          storyTitle={story.title}
          storyUrl={window.location.href}
        />
      )}
    </StoryContainer>
  );
};

export default StoryView;